class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.searchRequestId = 0;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
    this.isHeaderSearch = this.closest('header') !== null;
    this.recentSearches = this.querySelector('[data-recent-searches]');
    this.recentSearchesList = this.querySelector('[data-recent-searches-list]');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.renderRecentSearches();

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.querySelector('form')?.addEventListener('submit', () => this.saveRecentSearch(this.getQuery()));
    this.recentSearches?.addEventListener('click', this.handleRecentSearchClick.bind(this));
    this.searchContent?.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.saveRecentSearch(this.getQuery());
    });

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  getRecentSearches() {
    try {
      const searches = JSON.parse(localStorage.getItem('header-recent-searches') || '[]');
      return Array.isArray(searches) ? searches.filter(Boolean).slice(0, 4) : [];
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(query) {
    const value = String(query || '').trim();
    if (!value) return;
    const searches = this.getRecentSearches().filter((item) => item.toLowerCase() !== value.toLowerCase());
    searches.unshift(value);
    localStorage.setItem('header-recent-searches', JSON.stringify(searches.slice(0, 4)));
  }

  renderRecentSearches() {
    if (!this.isHeaderSearch || !this.recentSearches || !this.recentSearchesList) return;
    const searches = this.getRecentSearches();
    this.recentSearchesList.replaceChildren();

    searches.forEach((query) => {
      const item = document.createElement('li');
      item.className = 'header-search-history__item';

      const link = document.createElement('a');
      const url = new URL(this.recentSearches.dataset.searchUrl || '/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('q', query);
      link.href = url.toString();
      link.textContent = query;
      link.addEventListener('click', () => this.saveRecentSearch(query));

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.dataset.removeRecentSearch = query;
      removeButton.setAttribute('aria-label', `Remove ${query} from recent searches`);
      const iconTemplate = this.querySelector('[data-recent-search-remove-icon]');
      if (iconTemplate?.content) removeButton.appendChild(iconTemplate.content.cloneNode(true));

      item.append(link, removeButton);
      this.recentSearchesList.appendChild(item);
    });

    this.recentSearches.hidden = searches.length === 0;
    this.searchRecommendationEmpty = searches.length === 0;
  }

  handleRecentSearchClick(event) {
    const removeButton = event.target.closest('[data-remove-recent-search]');
    if (removeButton) {
      const query = removeButton.dataset.removeRecentSearch || '';
      const searches = this.getRecentSearches().filter((item) => item !== query);
      localStorage.setItem('header-recent-searches', JSON.stringify(searches));
      this.renderRecentSearches();
      if (!searches.length) this.toggleSearchState(false);
      return;
    }

    if (event.target.closest('[data-clear-recent-searches]')) {
      localStorage.removeItem('header-recent-searches');
      this.renderRecentSearches();
      this.toggleSearchState(false);
    }
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.cancelPendingSearch();
    this.input.value = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.resetButton.classList.remove(this.states.LOADING);
    this.renderRecentSearches();

    const showRecentSearches = this.isHeaderSearch && this.getRecentSearches().length > 0;
    this.searchContent?.classList.toggle('hidden', !showRecentSearches);
    this.toggleSearchState(showRecentSearches);
    this.input.focus();
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[type]', 'product,collection,query'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,tag,variants.sku,variants.barcode'
      ),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    this.cancelPendingSearch();
    const responseText = this.cachedMap.get(url);
    this.removeAttribute('loading');
    this.resetButton.classList.remove(this.states.LOADING);
    this.renderSearchResults(responseText);
    this.setAttribute('results', 'true');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    const requestId = ++this.searchRequestId;
    const controller = new AbortController();
    this.abortController = controller;
    this.setLoadingState(true);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== this.searchRequestId) return;
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (requestId !== this.searchRequestId) return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        if (requestId !== this.searchRequestId) return;
        this.abortController = null;
        this.setLoadingState(false);
      });
  }

  cancelPendingSearch() {
    this.searchRequestId += 1;
    this.abortController?.abort();
    this.abortController = null;
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

class SearchTypoResults {
  constructor(root) {
    this.root = root;
    this.term = String(root.dataset.searchTerm || '').trim();
    this.sectionId = root.dataset.searchSectionId;
    this.suggestion = root.querySelector('[data-search-suggestion]');
    this.suggestionLink = root.querySelector('[data-search-suggestion-link]');
    this.noMatch = root.querySelector('[data-search-no-match]');
    this.partial = root.querySelector('[data-search-partial]');
    this.partialContent = root.querySelector('[data-search-partial-content]');
    this.hasPartialResults = false;
  }

  async init() {
    if (!this.term || !this.sectionId || !this.suggestionLink || !this.partialContent) {
      if (this.noMatch) this.noMatch.hidden = false;
      if (this.partial) this.partial.hidden = true;
      return;
    }

    try {
      const suggestedTerm = await this.getSuggestedTerm();
      const partialTerm = suggestedTerm || this.term;

      const searchUrl = new URL(`${window.location.origin}${window.Shopify?.routes?.root || '/'}search`);
      searchUrl.searchParams.set('q', partialTerm);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[prefix]', 'last');

      if (suggestedTerm && suggestedTerm.toLowerCase() !== this.term.toLowerCase()) {
        this.suggestionLink.textContent = suggestedTerm;
        this.suggestionLink.href = searchUrl.toString();
        this.suggestion.hidden = false;
      }

      searchUrl.searchParams.set('section_id', this.sectionId);
      const response = await fetch(searchUrl.toString());
      if (!response.ok) return;

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const correctedResults = documentFragment.querySelector('[data-search-page]');
      const toolbar = correctedResults?.querySelector('.search-results-toolbar');
      const content = correctedResults?.querySelector('.collection__content');
      if (!content?.querySelector('#ProductsList .f-column')) return;

      this.partialContent.replaceChildren();
      if (toolbar) {
        toolbar.classList.remove('sticky-element');
        toolbar.style.removeProperty('--sticky-offset');
        this.partialContent.append(toolbar);
      }
      this.partialContent.append(content);
      this.partial.hidden = false;
      this.partial.classList.remove('is-resolving');
      this.partial.removeAttribute('aria-busy');
      this.hasPartialResults = true;
      if (this.noMatch) this.noMatch.hidden = true;
      this.root.classList.add('template-search--typo');
    } catch (error) {
      console.error('Unable to load typo search results:', error);
    } finally {
      if (!this.hasPartialResults) {
        if (this.noMatch) this.noMatch.hidden = false;
        if (this.partial) this.partial.hidden = true;
      }
    }
  }

  async getSuggestedTerm() {
    const suggestUrl = new URL(`${window.location.origin}${window.Shopify?.routes?.root || '/'}search/suggest.json`);
    suggestUrl.searchParams.set('q', this.term);
    suggestUrl.searchParams.set('resources[type]', 'query');
    suggestUrl.searchParams.set('resources[limit]', '1');

    const response = await fetch(suggestUrl.toString());
    if (!response.ok) return '';
    const payload = await response.json();
    return String(payload?.resources?.results?.queries?.[0]?.text || '').trim();
  }
}

document.querySelectorAll('[data-search-page].template-search--empty').forEach((root) => {
  new SearchTypoResults(root).init();
});
